/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBusinessSignUpConfig, IBusinessSignUpProps } from './business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps, BusinessSignUpRadioButtonOption } from './components/sign-up-radio-button';
import BusinessSignUpDropdown from './components/sign-up-dropdown';
import { getPrimaryProductInterestsAsync } from './actions/DataActionExtension.g';
import { AvaGetPrimaryProductInterestEntity } from './actions/DataServiceEntities.g';
import BusinessSignUpDropdown2 from './components/sign-up-dropdown2';

// export interface CommercePropertyValue {
//     BooleanValue?: boolean;
//     ByteValue?: number;
//     DateTimeOffsetValue?: Date;
//     DecimalValue?: number;
//     IntegerValue?: number;
//     LongValue?: number;
//     StringValue?: string;
// }
// export interface CommerceProperty {
//     Key?: string;
//     Value?: CommercePropertyValue;
// }
// export interface BusinessPartnerProspect {
//     FirstName?: string;
//     LastName?: string;
//     CompanyName?: string;
//     Address?: Address;
//     JobTitle?: string;
//     Department?: string;
//     CompanySize?: number;
//     Comments?: string;
//     LastOperationId?: string;
//     ProspectTypeValue?: number;
//     ExtensionProperties?: CommerceProperty[];
// }

export interface IBusinessSignUpExtendedProps extends IBusinessSignUpProps<IBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    productInterests: AvaGetPrimaryProductInterestEntity[];
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    dropdownOptions?: AvaGetPrimaryProductInterestEntity[];
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    RadioButton = 'radiobutton',
    DropDown = 'dropdown'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    ProspectType = 'ProspectType',
    OrganizationId = 'B2BOrganizationId',
    IsExistingCustomer = 'IsExistingCustomer',
    PrimaryProductInterest = 'PrimaryProductInterest',
    InterestedIn = 'InterestedIn'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    productInterests: AvaGetPrimaryProductInterestEntity[],
    name: string,
    resources: any,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            radioButtonOptions,
            radioButtonGroupClass
            // dropdownOptions
        } = element;

        const sizeDropdownOptions = [
            '1-50',
            '50-100',
            '100-150',
            '150-200',
            '200-250',
            '250-300',
            '300-350',
            '350-400',
            '400-450',
            '450-500',
            '500+'
        ];

        let isHidden = false;
        if (type === InputType.Text || type === InputType.DropDown) {
            // console.log('inputs[1].value', inputs[1].value);

            if (inputs[1].value === 'Yes') {
                isHidden = [
                    InputID.JobTitle,
                    InputID.Size,
                    InputID.Department,
                    InputID.PrimaryProductInterest,
                    InputID.InterestedIn
                ].includes(id as InputID)
                    ? true
                    : false;
            } else {
                isHidden = [InputID.OrganizationId].includes(id as InputID) ? true : false;
            }
        }

        // console.log('className and id and type ishiden inputs[1].value', className, id, type, isHidden, inputs[1].value);
        const cn = isHidden
            ? classnames(className, `${className}-${id}`, customClass, 'hidden')
            : classnames(className, `${className}-${id}`, customClass);
        // console.log('cn', cn);

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: cn
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.RadioButton) {
            baseSignUpItem.input = (
                <div className={radioButtonGroupClass}>
                    {radioButtonOptions &&
                        radioButtonOptions.map((element, index) => {
                            return <BusinessSignUpRadioButtonOption key={index} {...element}></BusinessSignUpRadioButtonOption>;
                        })}
                </div>
            );
        } else if (type === InputType.DropDown) {
            if (id === InputID.Size) {
                baseSignUpItem.input = (
                    <div>
                        <BusinessSignUpDropdown2 id={id} value={value} dropdownOptions={sizeDropdownOptions} onChange={onInputChange} />
                    </div>
                );
            } else {
                baseSignUpItem.input = (
                    <div>
                        <BusinessSignUpDropdown
                            id={id}
                            value={value}
                            dropdownOptions={productInterests}
                            onChange={onInputChange}
                            resources={resources}
                        />
                    </div>
                );
            }
        } else {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference,
                        ...(id === InputID.InterestedIn && { name })
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IBusinessSignUpProps<IBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';
    private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';
    // private readonly prospectDropdownName: string = 'business-sign-up-dropdown';
    // private readonly prospectDropdownName2: string = 'business-sign-up-dropdown2';
    private readonly prospectInterestedInName: string = 'business-sign-up-interested-in';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.ProspectType,
            type: InputType.RadioButton,
            label: this.props.resources.prospectTypeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100',
            radioButtonGroupClass: 'radio-option-container',
            radioButtonOptions: [
                {
                    id: `${InputID.ProspectType}-buyer`,
                    name: this.prospectRadioButtonName,
                    value: '1', // Buyer
                    label: this.props.resources.prospectTypeBuyerLabel,
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.ProspectType}-seller`,
                    name: this.prospectRadioButtonName,
                    value: '2', // Seller
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: this.props.resources.prospectTypeSellerLabel,
                    onChange: this._onInputChange.bind(this)
                }
            ]
        },
        //IsExistingCustomer
        {
            id: InputID.IsExistingCustomer,
            type: InputType.RadioButton,
            label: this.props.resources.isExistingCustomerLabel,
            value: 'Yes',
            isRequired: true,
            customClass: 'width-100',
            radioButtonGroupClass: 'radio-option-container',
            radioButtonOptions: [
                {
                    id: `${InputID.IsExistingCustomer}-yes`,
                    name: this.prospectRadioButtonName,
                    value: 'Yes',
                    label: this.props.resources.isExistingCustomerYesLabel,
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.IsExistingCustomer}-no`,
                    name: this.prospectRadioButtonName,
                    value: 'No',
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: this.props.resources.isExistingCustomerNoLabel,
                    onChange: this._onInputChange.bind(this)
                }
                // {
                //     id: `${InputID.IsExistingCustomer}-default`,
                //     name: this.prospectRadioButtonName,
                //     value: 'Default',
                //     className: `${this.moduleClassName}__form-item-radio-button-option`,
                //     label: this.props.resources.isExistingCustomerDefaultLabel,
                //     onChange: this._onInputChange.bind(this)
                // }
            ]
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.OrganizationId,
            type: InputType.Text,
            label: this.props.resources.organizationIDLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.jobTitleLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        // {
        //     id: InputID.Size,
        //     type: InputType.Text,
        //     label: this.props.resources.companySizeLabel,
        //     value: '',
        //     isRequired: false,
        //     customClass: 'width-50'
        // },
        {
            id: InputID.Size,
            type: InputType.DropDown,
            label: this.props.resources.companySizeLabel,
            value: '1-50',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.PrimaryProductInterest,
            type: InputType.DropDown,
            label: this.props.resources.primaryProductInterestLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.InterestedIn,
            type: InputType.Text,
            label: this.props.resources.interestedInLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);
        // console.log('this.props', this.props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            productInterests: [
                // {
                //     primaryProductInterest: 'Item-1',
                //     ExtensionProperties: []
                // },
                // {
                //     primaryProductInterest: 'Item-2',
                //     ExtensionProperties: []
                // },
                // {
                //     primaryProductInterest: 'Item-3',
                //     ExtensionProperties: []
                // }
            ]
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        getPrimaryProductInterestsAsync({
            callerContext: this.props.context.actionContext,
            queryResultSettings: {
                Paging: {
                    Top: 1000
                }
            }
        }).then(result => {
            let res = [];
            res = result?.slice();
            this.setState({ productInterests: res });
        });
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                this.state.productInterests,
                this.prospectInterestedInName,
                this.props.resources,
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            // console.log('event.target.name', event.target.name, event.target.value);
            if (event.target.name === this.prospectInterestedInName) {
                if (event.target.value.length > 300) {
                    return;
                }
            }

            if (item.type === InputType.RadioButton && event.target.name === this.prospectRadioButtonName) {
                item.value = event.target.value;
            }
            // else if (item.type === InputType.DropDown && event.target.name === this.prospectDropdownName) {
            //     item.value = event.target.value;
            //     console.log('in 1');
            // } else if (item.type === InputType.DropDown && event.target.name === this.prospectDropdownName2) {
            //     console.log('in 2');

            //     item.value = event.target.value;
            // }
            else if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });
        // console.log('newForm', newForm);

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState(
                {
                    formItems: newForm,
                    addressHasErrors: errors || !Boolean(currentAddress?.Phone), // set addressHasErrors true also if phone number not entered
                    showAddressErrors: false
                },
                () => {
                    this.setState({ isReady: this._isFormReady() });
                }
            );
        } else {
            this.setState(
                {
                    addressHasErrors: true //make it true as phone number not entered
                },
                () => {
                    this.setState({ isReady: this._isFormReady() });
                }
            );
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        if (this.state.addressHasErrors) {
            return false;
        }

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            } else if (
                // have kept OrganizationId's isRequired as false
                currentInput.id === InputID.OrganizationId &&
                formItems[1].value === 'Yes' && // to check is required only if Yes is selected for isExistingCustomer
                (currentInput.value === '' || currentInput.value === undefined)
            ) {
                return false;
            } else if (
                // have kept OrganizationId's isRequired as false
                (currentInput.id === InputID.InterestedIn || currentInput.id === InputID.PrimaryProductInterest) &&
                formItems[1].value === 'No' && // to check is required only if No is selected for isExistingCustomer
                (currentInput.value === '' || currentInput.value === undefined)
            ) {
                return false;
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        const { config, context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);
        const prospectType = this._getItem(InputID.ProspectType);
        const organizationId = this._getItem(InputID.OrganizationId);
        const isExistingCustomer = this._getItem(InputID.IsExistingCustomer);
        const primaryProductInterest = this._getItem(InputID.PrimaryProductInterest);
        const interestedIn = this._getItem(InputID.InterestedIn);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            Department: department && department.value,
            CompanySize: Number(companySize && companySize.value.split('-')[1]) || 0,
            Comments: comments && comments.value,
            ProspectTypeValue: (prospectType && Number.parseInt(prospectType.value, 10)) || 0,
            ExtensionProperties: [
                ...(isExistingCustomer?.value === 'Yes' && organizationId
                    ? [
                          {
                              Key: organizationId.id,
                              Value: {
                                  StringValue: organizationId.value
                              }
                          }
                      ]
                    : []), // Conditionally add organizationId object only if it exists
                {
                    Key: isExistingCustomer?.id,
                    Value: {
                        StringValue: isExistingCustomer?.value
                    }
                },
                ...(isExistingCustomer?.value === 'No' && primaryProductInterest
                    ? [
                          {
                              Key: primaryProductInterest.id,
                              Value: {
                                  StringValue: primaryProductInterest.value
                              }
                          }
                      ]
                    : []), // Conditionally add organizationId object only if it exists,
                ...(isExistingCustomer?.value === 'No' && interestedIn
                    ? [
                          {
                              Key: interestedIn.id,
                              Value: {
                                  StringValue: interestedIn.value
                              }
                          }
                      ]
                    : []) // Conditionally add organizationId object only if it exists,
            ]
            // OrganizationId: organizationId && organizationId.value
        };

        this.setState({
            isReady: false
        });

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(() => {
                const successPageURL =
                    (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                    getUrlSync('home', this.props.context.actionContext) ||
                    '';
                window.location.href = successPageURL;
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
    //ExtensionProperties need to add key value pair to the object
    // private readonly _customGetItem = (id: string, isAddress: boolean = false) => {
    //     if (isAddress) {
    //         return this.state.formItems.find(item => {
    //             return item.id === id && item.address;
    //         });
    //     }
    //     return this.state.formItems.find(item => {
    //         return item.id === id && item.value !== '';
    //     });
    // };
}

export default BusinessSignUp;
